import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './PerfilPage.module.css';

// ── Evaluador de fortaleza de contraseña ─────────────────────
function evaluarPassword(pass) {
  if (!pass) return { nivel: 0, label: '', color: '' };
  let puntos = 0;
  if (pass.length >= 8)  puntos++;
  if (pass.length >= 12) puntos++;
  if (/[A-Z]/.test(pass)) puntos++;
  if (/[0-9]/.test(pass)) puntos++;
  if (/[^A-Za-z0-9]/.test(pass)) puntos++;

  const niveles = [
    { nivel: 1, label: 'Muy débil',  color: '#c0392b' },
    { nivel: 2, label: 'Débil',      color: '#e67e22' },
    { nivel: 3, label: 'Normal',     color: '#f1c40f' },
    { nivel: 4, label: 'Segura',     color: '#2ecc71' },
    { nivel: 5, label: 'Muy segura', color: '#27ae60' },
  ];
  return niveles[Math.min(puntos, 4)];
}

export default function PerfilPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [perfil, setPerfil]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [pass, setPass]             = useState('');
  const [passRep, setPassRep]       = useState('');
  const [verPass, setVerPass]       = useState(false);
  const [verPassRep, setVerPassRep] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [msgPass, setMsgPass]       = useState('');
  const [avatarPreview, setAvatarPreview]     = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [msgAvatar, setMsgAvatar]             = useState('');
  const fileRef = useRef();
  const [notifAnuncios, setNotifAnuncios] = useState(true);
  const [notifMensajes, setNotifMensajes] = useState(true);
  const [savingNotif, setSavingNotif]     = useState(false);
  const [msgNotif, setMsgNotif]           = useState('');

  const fortaleza = evaluarPassword(pass);

  const cargar = () => {
    setLoading(true);
    api.get('/usuarios/me')
      .then(r => {
        const data = r.data.data;
        setPerfil(data);
        if (data.avatar_url) setAvatarPreview(data.avatar_url);
        setNotifAnuncios(data.notif_anuncios === undefined ? true : !!parseInt(data.notif_anuncios));
        setNotifMensajes(data.notif_mensajes === undefined ? true : !!parseInt(data.notif_mensajes));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, []);

  const guardarPassword = async e => {
    e.preventDefault();
    if (pass !== passRep) { setMsgPass('Las contraseñas no coinciden'); return; }
    if (pass.length < 6)  { setMsgPass('Mínimo 6 caracteres'); return; }
    setSavingPass(true); setMsgPass('');
    try {
      await api.post('/usuarios/me', { _method: 'PUT', password: pass, passwordRep: passRep });
      setMsgPass('✓ Contraseña actualizada correctamente');
      setPass(''); setPassRep('');
    } catch (err) {
      setMsgPass(err.response?.data?.error || 'Error al guardar');
    } finally { setSavingPass(false); }
  };

  const guardarNotificaciones = async (nuevoAnuncios, nuevoMensajes) => {
    setSavingNotif(true); setMsgNotif('');
    try {
      await api.post('/usuarios/me', {
        _method: 'PUT',
        notif_anuncios: nuevoAnuncios ? 1 : 0,
        notif_mensajes: nuevoMensajes ? 1 : 0,
      });
      setMsgNotif('✓ Preferencias guardadas');
      setTimeout(() => setMsgNotif(''), 2000);
    } catch {
      setMsgNotif('Error al guardar preferencias');
    } finally { setSavingNotif(false); }
  };

  const toggleAnuncios = async () => {
    const nuevo = !notifAnuncios;
    setNotifAnuncios(nuevo);
    await guardarNotificaciones(nuevo, notifMensajes);
  };

  const toggleMensajes = async () => {
    const nuevo = !notifMensajes;
    setNotifMensajes(nuevo);
    await guardarNotificaciones(notifAnuncios, nuevo);
  };

  const onFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const subirAvatar = async () => {
    const file = fileRef.current?.files[0];
    if (!file) return;
    setUploadingAvatar(true); setMsgAvatar('');
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const r = await api.post('/usuarios/me/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatarPreview(r.data.data.avatar_url + '?t=' + Date.now());
      setMsgAvatar('✓ Foto actualizada correctamente');
      cargar();
    } catch (err) {
      setMsgAvatar(err.response?.data?.error || 'Error al subir la imagen');
    } finally { setUploadingAvatar(false); }
  };

  const rolLabel = r => ({ admin: '🛡️ Administrador', profesor: '👨‍🏫 Profesor', alumno: '👩‍🎓 Alumno' }[r] || r);

  if (loading) return <div className={styles.loading}>Cargando perfil...</div>;
  const iniciales = perfil ? `${perfil.nombre?.[0] ?? ''}${perfil.apellidos?.[0] ?? ''}` : '';

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>← Volver</button>
      <div className={styles.layout}>
        {/* Panel izquierdo */}
        <div className={styles.leftPanel}>
          <div className={styles.avatarSection}>
            <div className={styles.avatarWrap}>
              {avatarPreview
                ? <img src={avatarPreview} alt="Avatar" className={styles.avatarImg} />
                : <div className={styles.avatarInitials}>{iniciales}</div>
              }
              <button className={styles.avatarEditBtn}
                onClick={() => fileRef.current.click()} title="Cambiar foto">📷</button>
            </div>
            <input ref={fileRef} type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }} onChange={onFileChange} />
            {fileRef.current?.files?.[0] && (
              <button className={styles.btnSubirFoto}
                onClick={subirAvatar} disabled={uploadingAvatar}>
                {uploadingAvatar ? 'Subiendo...' : '💾 Guardar foto'}
              </button>
            )}
            {msgAvatar && (
              <p className={msgAvatar.startsWith('✓') ? styles.success : styles.error}>{msgAvatar}</p>
            )}
          </div>
          <div className={styles.identidad}>
            <h2 className={styles.nombreCompleto}>{perfil?.nombre} {perfil?.apellidos}</h2>
            <span className={styles.rolBadge}>{rolLabel(perfil?.rol)}</span>
            <p className={styles.email}>{perfil?.email}</p>
            <p className={styles.miembro}>
              Miembro desde {new Date(perfil?.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>

        {/* Panel derecho */}
        <div className={styles.rightPanel}>
          {/* Datos personales */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              📋 Datos personales
              <span className={styles.readonlyNote}>Solo lectura — contacta con administración para modificarlos</span>
            </h3>
            <div className={styles.camposGrid}>
              <CampoReadonly label="Nombre"           valor={perfil?.nombre} />
              <CampoReadonly label="Apellidos"         valor={perfil?.apellidos} />
              <CampoReadonly label="DNI / NIF"         valor={perfil?.dni} />
              <CampoReadonly label="Fecha nacimiento"  valor={perfil?.fecha_nacimiento
                ? new Date(perfil.fecha_nacimiento).toLocaleDateString('es-ES') : null} />
              <CampoReadonly label="Calle / Dirección" valor={perfil?.calle} full />
              <CampoReadonly label="Código postal"     valor={perfil?.codigo_postal} />
              <CampoReadonly label="Provincia"         valor={perfil?.provincia} />
              <CampoReadonly label="País"              valor={perfil?.pais} />
              <CampoReadonly label="Email"             valor={perfil?.email} full />
              <CampoReadonly label="Rol"               valor={rolLabel(perfil?.rol)} />
            </div>
          </div>

          {/* Notificaciones */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>🔔 Notificaciones por email</h3>
            <p className={styles.notifDesc}>Controla qué emails quieres recibir de EduPortal UAL.</p>
            <div className={styles.notifList}>
              <div className={styles.notifItem}>
                <div className={styles.notifInfo}>
                  <p className={styles.notifTitulo}>📢 Anuncios del curso</p>
                  <p className={styles.notifSub}>Recibirás un email cuando el profesor publique un nuevo anuncio.</p>
                </div>
                <button className={`${styles.toggle} ${notifAnuncios ? styles.toggleOn : styles.toggleOff}`}
                  onClick={toggleAnuncios} disabled={savingNotif}>
                  <span className={styles.toggleKnob} />
                </button>
              </div>
              <div className={styles.notifItem}>
                <div className={styles.notifInfo}>
                  <p className={styles.notifTitulo}>✉️ Resumen diario de mensajes</p>
                  <p className={styles.notifSub}>Recibirás un email a las 22:00 si tienes mensajes sin leer.</p>
                </div>
                <button className={`${styles.toggle} ${notifMensajes ? styles.toggleOn : styles.toggleOff}`}
                  onClick={toggleMensajes} disabled={savingNotif}>
                  <span className={styles.toggleKnob} />
                </button>
              </div>
            </div>
            {msgNotif && <p className={msgNotif.startsWith('✓') ? styles.success : styles.error}>{msgNotif}</p>}
          </div>

          {/* Cambiar contraseña */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>🔒 Cambiar contraseña</h3>
            <form onSubmit={guardarPassword} className={styles.passForm}>
              <label className={styles.label}>
                Nueva contraseña
                <div className={styles.passWrap}>
                  <input type={verPass ? 'text' : 'password'} className={styles.passInput}
                    placeholder="Mínimo 6 caracteres" value={pass}
                    onChange={e => setPass(e.target.value)} required />
                  <button type="button" className={styles.eyeBtn}
                    onClick={() => setVerPass(p => !p)}>{verPass ? '🙈' : '👁'}</button>
                </div>
              </label>

              {/* Barra de fortaleza */}
              {pass && (
                <div className={styles.strengthWrap}>
                  <div className={styles.strengthBar}>
                    {[1,2,3,4,5].map(n => (
                      <div key={n} className={styles.strengthSegment}
                        style={{ background: n <= fortaleza.nivel ? fortaleza.color : 'var(--border)' }} />
                    ))}
                  </div>
                  <span className={styles.strengthLabel} style={{ color: fortaleza.color }}>
                    {fortaleza.label}
                  </span>
                </div>
              )}

              <label className={styles.label}>
                Repite la nueva contraseña
                <div className={styles.passWrap}>
                  <input type={verPassRep ? 'text' : 'password'}
                    className={`${styles.passInput} ${pass && passRep && pass !== passRep ? styles.passError : ''}`}
                    placeholder="Repite la contraseña" value={passRep}
                    onChange={e => setPassRep(e.target.value)} required />
                  <button type="button" className={styles.eyeBtn}
                    onClick={() => setVerPassRep(p => !p)}>{verPassRep ? '🙈' : '👁'}</button>
                </div>
                {pass && passRep && pass !== passRep && (
                  <span className={styles.passErrorMsg}>Las contraseñas no coinciden</span>
                )}
              </label>

              {msgPass && <p className={msgPass.startsWith('✓') ? styles.success : styles.error}>{msgPass}</p>}
              <button type="submit" className={styles.btnGuardar}
                disabled={savingPass || !pass || !passRep || pass !== passRep}>
                {savingPass ? 'Guardando...' : '💾 Actualizar contraseña'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function CampoReadonly({ label, valor, full }) {
  return (
    <div className={`${styles.campo} ${full ? styles.campoFull : ''}`}>
      <span className={styles.campoLabel}>{label}</span>
      <div className={styles.campoValor}>
        {valor || <span className={styles.campoVacio}>Sin datos</span>}
      </div>
    </div>
  );
}
